import * as BABYLON from '@babylonjs/core';
import type { RewardResult } from './rewards';
import { EventsManager } from '@game-libs/events-manager';
import { DungeonEvents } from '../../types/game-events';

const DEFAULT_CHEST_SIZE = 1.6;

export enum RewardId {
  GOLD = 'GOLD',
  TORCH = 'TORCH',
  GOLDEN_KEY = 'GOLDEN_KEY',
  GRAY_KEY = 'GRAY_KEY',
  NOTHING = 'NOTHING',
}

const REWARD_IMAGE_MAP: Partial<Record<RewardId, string>> = {
  [RewardId.GOLD]: 'dungeon-crawler/rewards/gold_reward.png',
  [RewardId.TORCH]: 'dungeon-crawler/rewards/torch_reward.png',
  [RewardId.GOLDEN_KEY]: 'dungeon-crawler/rewards/gold_key_reward.png',
  [RewardId.GRAY_KEY]: 'dungeon-crawler/rewards/gray_key_reward.png',
};

export interface ChestConfig {
  position: BABYLON.Vector3;
  closedMaterial: BABYLON.StandardMaterial;
  openMaterial: BABYLON.StandardMaterial;
  scene: BABYLON.Scene;
  camera: BABYLON.Camera;
  reward: RewardResult;
  /** If true when the chest is opened, the chest gives nothing (empty). */
  getIsFadingOut?: () => boolean;
  onRewardCollected?: (reward: RewardResult) => void;
  showDebug?: boolean;
}

export class DungeonChest {
  private mesh: BABYLON.Mesh;
  private collisionBox: BABYLON.Mesh;
  private isOpen: boolean = false;
  private hasBeenOpened: boolean = false;
  private position: BABYLON.Vector3;
  private scene: BABYLON.Scene;
  private camera: BABYLON.Camera;
  private showDebug: boolean;
  private pointerObserver: BABYLON.Nullable<BABYLON.Observer<BABYLON.PointerInfo>> = null;
  private _disposed = false;
  private closedMaterial: BABYLON.StandardMaterial;
  private openMaterial: BABYLON.StandardMaterial;
  private reward: RewardResult;
  private getIsFadingOut?: () => boolean;
  private onRewardCollected?: (reward: RewardResult) => void;
  private eventsManager = EventsManager();

  private readonly _toChest = new BABYLON.Vector3();
  private readonly _forwardXZ = new BABYLON.Vector3();

  constructor(config: ChestConfig) {
    this.position = config.position;
    this.scene = config.scene;
    this.camera = config.camera;
    this.showDebug = config.showDebug || false;
    this.closedMaterial = config.closedMaterial;
    this.openMaterial = config.openMaterial;
    this.reward = config.reward;
    this.getIsFadingOut = config.getIsFadingOut;
    this.onRewardCollected = config.onRewardCollected;

    // Create the visual chest mesh (billboard plane that faces camera)
    this.mesh = BABYLON.MeshBuilder.CreateGround(
      `chest_${this.position.x}_${this.position.z}`,
      { width: DEFAULT_CHEST_SIZE, height: DEFAULT_CHEST_SIZE },
      config.scene
    );
    this.mesh.rotation.x = -Math.PI / 2; // Make it vertical
    this.mesh.material = this.closedMaterial;
    this.mesh.position = this.position.clone();
    this.mesh.position.y = 1; // Slightly above floor so it's visible and clickable
    this.mesh.isPickable = true;
    this.mesh.cullingStrategy = BABYLON.Mesh.OCCLUSION_TYPE_OPTIMISTIC;

    // Collision box so MazeCameraController rays block movement when chest is closed; disabled when opened
    this.collisionBox = BABYLON.MeshBuilder.CreateBox(
      `chestCollision_${this.position.x}_${this.position.z}`,
      { width: DEFAULT_CHEST_SIZE, height: 2, depth: DEFAULT_CHEST_SIZE },
      config.scene
    );
    this.collisionBox.position.copyFrom(this.mesh.position);
    this.collisionBox.checkCollisions = true;
    this.collisionBox.isVisible = this.showDebug;
    this.collisionBox.isPickable = true;

    // Set up pointer observable to handle clicks on this specific chest
    this.pointerObserver = this.scene.onPointerObservable.add((pointerInfo) => {
      if (pointerInfo.type !== BABYLON.PointerEventTypes.POINTERDOWN) return;

      const evt = pointerInfo.event;
      const canvas = this.scene.getEngine().getRenderingCanvas();
      if (!canvas || !evt) return;

      // Get click coordinates
      const rect = canvas.getBoundingClientRect();
      const scaleX = canvas.width / rect.width;
      const scaleY = canvas.height / rect.height;
      const x = (evt.clientX - rect.left) * scaleX;
      const y = (evt.clientY - rect.top) * scaleY;

      // Pick to see what was clicked
      const pickInfo = this.scene.pick(x, y);

      // Only open if THIS chest was clicked (mesh or collision box)
      if (!pickInfo.hit) return;
      if (pickInfo.pickedMesh !== this.mesh && pickInfo.pickedMesh !== this.collisionBox) return;

      // Check if player is facing the chest
      if (!this.isPlayerFacing(5)) {
        return;
      }

      this.open();
    });
  }

  /**
   * Check if player is facing this chest (position-based, so it works regardless of mesh height).
   * Camera only rotates in XZ, so we check if the chest is in front in the XZ plane.
   */
  private isPlayerFacing(maxDistance: number = 5): boolean {
    const camPos = this.camera.position;
    const chestPos = this.mesh.position;

    const dx = chestPos.x - camPos.x;
    const dz = chestPos.z - camPos.z;
    const distSq = dx * dx + dz * dz;
    if (distSq > maxDistance * maxDistance) return false;
    if (distSq < 1e-6) return true; // Right on top of chest

    this._toChest.set(dx, 0, dz);
    const lenToChest = this._toChest.length();
    if (lenToChest < 1e-6) return true;
    this._toChest.scaleInPlace(1 / lenToChest);

    const ray = this.camera.getForwardRay(1);
    if (!ray?.direction) return false;
    this._forwardXZ.set(ray.direction.x, 0, ray.direction.z);
    const lenForward = this._forwardXZ.length();
    if (lenForward < 1e-6) return false;
    this._forwardXZ.scaleInPlace(1 / lenForward);

    const dot = BABYLON.Vector3.Dot(this._forwardXZ, this._toChest);
    return dot > 0.5; // ~60° cone in front of player
  }

  /**
   * Opens the chest - plays reward animation then gives reward.
   * If the torch fade has already started, this chest is always empty.
   */
  public open(): void {
    if (this.hasBeenOpened) return;

    const effectiveReward = this.getIsFadingOut?.() ? { id: RewardId.NOTHING, amount: 0 } as RewardResult : this.reward;

    this.eventsManager.emit(DungeonEvents.OPEN_CHEST);
    this.isOpen = true;
    this.hasBeenOpened = true;

    this.collisionBox.checkCollisions = false; // Allow player to walk through once opened
    this.mesh.material = this.openMaterial;

    // No longer interactable; stop listening to pointer events
    if (this.pointerObserver) {
      this.scene.onPointerObservable.remove(this.pointerObserver);
      this.pointerObserver = null;
    }

    this.eventsManager.emit(DungeonEvents.PICK_ITEM, { itemId: effectiveReward.id });

    // Play the floating reward animation, then notify parent
    this.playRewardAnimation(() => {
      if (this.onRewardCollected) {
        this.onRewardCollected(effectiveReward);
      }
    }, effectiveReward);
  }

  /**
   * Animates a reward image floating upward and fading out above the chest.
   * Uses NEAREST sampling and ALPHATEST (same as enemy sprites) for crisp pixel-art rendering.
   * Calls onComplete when the animation finishes.
   * @param rewardToDisplay - reward to show (e.g. NOTHING for empty = no float)
   */
  private playRewardAnimation(onComplete?: () => void, rewardToDisplay?: RewardResult): void {
    const reward = rewardToDisplay ?? this.reward;
    const imagePath = REWARD_IMAGE_MAP[reward.id as RewardId];
    if (!imagePath) {
      onComplete?.();
      return;
    }

    // Create a small plane mesh that will float upward
    const floatMesh = BABYLON.MeshBuilder.CreatePlane(
      `rewardFloat_${this.position.x}_${this.position.z}`,
      { width: 1.2, height: 1.2 },
      this.scene
    );

    // Start just above the chest
    floatMesh.position.copyFrom(this.mesh.position);
    floatMesh.position.y += 0.5;
    floatMesh.billboardMode = BABYLON.Mesh.BILLBOARDMODE_ALL;
    floatMesh.isPickable = false;

    // NEAREST_SAMPLINGMODE = crisp pixel-art, no bilinear blurring (same as enemy sprites)
    const texture = new BABYLON.Texture(
      imagePath,
      this.scene,
      false,
      true,
      BABYLON.Texture.NEAREST_SAMPLINGMODE
    );
    texture.hasAlpha = true;

    // ALPHATEST + alphaCutOff = hard transparent edges, no anti-aliasing blur (same as enemy sprites)
    const mat = new BABYLON.StandardMaterial(`rewardFloatMat_${Date.now()}`, this.scene);
    mat.diffuseTexture = texture;
    mat.useAlphaFromDiffuseTexture = true;
    mat.transparencyMode = BABYLON.Material.MATERIAL_ALPHATEST;
    mat.alphaCutOff = 0.5;
    mat.backFaceCulling = false;
    mat.emissiveColor = BABYLON.Color3.White(); // Unlit so it's always bright
    mat.specularColor = BABYLON.Color3.Black();
    floatMesh.material = mat;

    // Animation parameters
    const DURATION_MS = 900;
    const RISE_AMOUNT = 2.0; // Units to float upward
    const startY = floatMesh.position.y;
    const startTime = performance.now();

    const observer = this.scene.onBeforeRenderObservable.add(() => {
      if (this._disposed) {
        cleanup();
        return;
      }

      const elapsed = performance.now() - startTime;
      const t = Math.min(elapsed / DURATION_MS, 1); // 0 → 1

      // Ease out: fast start, slow finish
      const eased = 1 - Math.pow(1 - t, 2);
      floatMesh.position.y = startY + eased * RISE_AMOUNT;

      // Fade out in the second half via stochastic dithering —
      // ALPHATEST doesn't support smooth alpha, so we flicker visibility
      // at decreasing probability to simulate a fade without blurring the sprite.
      if (t >= 0.4) {
        const fadeProgress = (t - 0.4) / 0.6; // 0 → 1 over the fade window
        floatMesh.isVisible = Math.random() > fadeProgress;
      }

      if (t >= 1) {
        cleanup();
        onComplete?.();
      }
    });

    const cleanup = () => {
      this.scene.onBeforeRenderObservable.remove(observer);
      texture.dispose();
      mat.dispose();
      floatMesh.dispose();
    };
  }

  /**
   * Rotate to face the camera (billboard effect)
   */
  public update(camera: BABYLON.Camera): void {
    const dx = camera.position.x - this.mesh.position.x;
    const dz = camera.position.z - this.mesh.position.z;
    this.mesh.rotation.y = Math.atan2(dx, dz);
  }

  public getIsOpen(): boolean {
    return this.isOpen;
  }

  public getPickableMeshes(): BABYLON.Mesh[] {
    return [this.mesh, this.collisionBox];
  }

  public getPosition(): BABYLON.Vector3 {
    return this.position;
  }

  /**
   * Clean up resources
   */
  public dispose(): void {
    if (this._disposed) return;
    this._disposed = true;

    // Remove pointer observer
    if (this.pointerObserver) {
      this.scene.onPointerObservable.remove(this.pointerObserver);
      this.pointerObserver = null;
    }

    this.collisionBox.dispose();
    this.mesh.dispose();
  }
}